// web-frontend/src/shared/stores/appStore.js
// 🔥 웹 전용 (localStorage 사용, React Native 의존성 완전 제거)
// 🔥🔥🔥 2025-12-29 수정: ThemeContext와 테마 클래스명 통일 (theme-light/theme-dark)

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { LANGUAGES, THEMES, APP_CONFIG } from '../constants';
import i18nService from '../services/i18n';
import logger from '../utils/logger';
import { getDefaultLanguage } from '../config/tenant';

const getTenantDefaultLanguage = () => {
  try {
    return getDefaultLanguage() || 'ko';
  } catch (error) {
    logger.warn('[AppStore] tenant default language fallback:', error);
    return 'ko';
  }
};

const getInitialLanguage = () => (
  i18nService.getCurrentLanguage?.() || getTenantDefaultLanguage()
);

/**
 * 전역 앱 상태 관리 스토어 (웹 전용)
 * 
 * 주요 기능:
 * - 언어 설정 및 i18nService와 동기화
 * - 테마 설정 (라이트/다크/시스템)
 * - 네트워크 상태 모니터링
 * - 전역 로딩/에러 상태 관리
 * - 대시보드 통계 관리
 * 
 * 특징:
 * - localStorage를 사용한 상태 영속화
 * - React Native 의존성 완전 제거
 * - 웹 브라우저 API만 사용
 * 
 * 🔥 2025-12-29 수정:
 * - ThemeContext와 클래스명 통일 (theme-light, theme-dark 사용)
 * - 테마 적용 로직 단순화 (ThemeContext가 주 관리자)
 */

const useAppStore = create(
  persist(
    (set, get) => ({
      // ========================================
      // 📊 상태 (State)
      // ========================================
      
      // 다국어 설정
      // 🔥 [2026-05-07] 문자열 코드로 저장한다 (예: 'en'). 과거에는 LANGUAGES.EN
      // (= {code, name, flag} 객체) 를 그대로 저장했지만 모든 consumer 가
      // language === 'en' 처럼 문자열 비교를 하기 때문에 항상 실패하면서 한국어
      // fallback 으로 떨어졌다. 모든 consumer 가 string code 를 기대하므로 여기서
      // 일관되게 string 으로 저장한다.
      language: getInitialLanguage(),
      
      // 테마 설정
      theme: THEMES.LIGHT,
      
      // 네트워크 상태 (웹 브라우저 기준)
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      
      // 로딩 및 에러 상태
      isLoading: false,
      globalError: null,
      
      // 대시보드 통계 (HomeScreen 등에서 사용)
      dashboardStats: {
        carbonPoints: 0,
        totalMatches: 0,
        activeMeetings: 0,
        completedEvents: 0,
      },
      
      // 챗봇 액션 이벤트 버스 (ChatbotModal → BoothMapPage 등)
      chatbotAction: null,

      // 앱 정보
      appVersion: APP_CONFIG.version || '1.0.0',
      lastSync: null,
      
      // ========================================
      // 🎬 액션 (Actions)
      // ========================================
      
      /**
       * 🌐 언어 변경 (i18nService와 동기화)
       * @param {string} language - 변경할 언어 코드 (ko, en, ja, zh 등)
       */
      setLanguage: async (language) => {
        try {
          logger.debug('🌐 [AppStore] 언어 변경 시도:', language);

          // 1. 입력 정규화: 객체로 들어오는 legacy 호출 (LANGUAGES.KO 등) 도 안전하게
          //    string code 로 변환한다.
          let langCode = language;
          if (language && typeof language === 'object' && typeof language.code === 'string') {
            langCode = language.code;
          }

          // 2. 지원하는 언어 (string code) 인지 확인
          const SUPPORTED = ['ko', 'en', 'ja', 'zh'];
          if (typeof langCode !== 'string' || !SUPPORTED.includes(langCode)) {
            logger.warn('⚠️ [AppStore] 지원하지 않는 언어:', language);
            return;
          }

          // 3. 현재 언어와 동일한지 확인 (불필요한 업데이트 방지)
          const currentLanguage = get().language;
          if (currentLanguage === langCode) {
            logger.debug('ℹ️ [AppStore] 이미 동일한 언어입니다:', langCode);
            return;
          }

          // 4. i18n 서비스 업데이트 (번역 파일 로드)
          await i18nService.changeLanguage(langCode);
          logger.debug('✅ [AppStore] i18nService 언어 변경 완료:', langCode);

          // 5. 스토어 상태 업데이트 (React 컴포넌트 리렌더링 트리거)
          set({ language: langCode });
          logger.debug('✅ [AppStore] 스토어 언어 상태 업데이트 완료:', langCode);

        } catch (error) {
          logger.error('❌ [AppStore] 언어 변경 실패:', error);
          get().setGlobalError('언어 변경에 실패했습니다.');
        }
      },
      
      /**
       * 🎨 테마 변경
       * 🔥 [수정] ThemeContext와 클래스명 통일
       * @param {string} theme - 변경할 테마 (light, dark, system)
       */
      setTheme: (theme) => {
        if (!Object.values(THEMES).includes(theme)) {
          logger.warn('⚠️ [AppStore] 지원하지 않는 테마:', theme);
          return;
        }
        
        logger.debug('🎨 [AppStore] 테마 변경:', theme);
        set({ theme });
        
        // 🔥🔥🔥 [핵심 수정] ThemeContext와 동일한 클래스명 사용
        if (typeof document !== 'undefined') {
          // 모든 테마 관련 클래스 제거
          document.body.classList.remove('theme-light', 'theme-dark', 'light-mode', 'dark-mode');
          
          let effectiveTheme = theme;
          
          // 시스템 테마인 경우 실제 테마 결정
          if (theme === THEMES.SYSTEM) {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            effectiveTheme = prefersDark ? THEMES.DARK : THEMES.LIGHT;
          }
          
          // 🔥 [수정] ThemeContext와 동일한 방식으로 클래스 적용
          if (effectiveTheme === THEMES.DARK) {
            document.body.classList.add('theme-dark', 'dark-mode');
            document.documentElement.setAttribute('data-theme', 'dark');
          } else {
            document.body.classList.add('theme-light', 'light-mode');
            document.documentElement.setAttribute('data-theme', 'light');
          }
          
          logger.debug(`🎨 [AppStore] DOM 테마 클래스 적용 완료: ${effectiveTheme}`);
        }
      },
      
      /**
       * 🔄 테마 토글 (라이트 ↔ 다크)
       */
      toggleTheme: () => {
        const currentTheme = get().theme;
        const newTheme = currentTheme === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT;
        get().setTheme(newTheme);
      },
      
      /**
       * 🌐 네트워크 상태 설정
       * @param {boolean} isOnline - 온라인 여부
       */
      setNetworkState: (isOnline) => {
        logger.debug(`🌐 [AppStore] 네트워크 상태 변경: ${isOnline ? '온라인' : '오프라인'}`);
        set({ isOnline });
        
        if (!isOnline && typeof document !== 'undefined') {
          logger.warn('⚠️ 인터넷 연결이 끊어졌습니다.');
        }
      },
      
      /**
       * ⏳ 전역 로딩 상태 설정
       * @param {boolean} isLoading - 로딩 여부
       */
      setGlobalLoading: (isLoading) => {
        set({ isLoading });
        if (isLoading) {
          logger.debug('⏳ [AppStore] 전역 로딩 시작');
        } else {
          logger.debug('✅ [AppStore] 전역 로딩 완료');
        }
      },
      
      /**
       * ❌ 전역 에러 설정
       * @param {string|Error} error - 에러 메시지 또는 에러 객체
       */
      setGlobalError: (error) => {
        const errorMessage = typeof error === 'string' ? error : error?.message || '알 수 없는 오류';
        logger.error('❌ [AppStore] 전역 에러:', errorMessage);
        set({
          globalError: errorMessage,
          isLoading: false
        });
      },

      /**
       * 🤖 챗봇 액션 디스패치 (ChatbotModal → 페이지 컴포넌트)
       * BoothMapPage 등이 이 값을 구독하여 맵 하이라이트/네비게이션 등 처리
       */
      dispatchChatbotAction: (action, payload) => {
        set({ chatbotAction: { action, payload, timestamp: Date.now() } });
      },
      clearChatbotAction: () => {
        set({ chatbotAction: null });
      },
      
      /**
       * 🧹 전역 에러 초기화
       */
      clearGlobalError: () => {
        logger.debug('🧹 [AppStore] 전역 에러 초기화');
        set({ globalError: null });
      },
      
      /**
       * 📊 대시보드 통계 업데이트
       * @param {Object} stats - 업데이트할 통계 객체
       */
      setDashboardStats: (stats) => {
        set((state) => ({
          dashboardStats: {
            ...state.dashboardStats,
            ...stats,
          },
        }));
        logger.debug('📊 [AppStore] 대시보드 통계 업데이트:', stats);
      },
      
      /**
       * 🔄 마지막 동기화 시간 업데이트
       */
      updateLastSync: () => {
        const now = new Date().toISOString();
        set({ lastSync: now });
        logger.debug('🔄 [AppStore] 마지막 동기화 시간 업데이트:', now);
      },
      
      /**
       * 🚀 앱 초기화
       * - 네트워크 상태 리스너 등록
       * - 초기 언어 설정 동기화
       * - 시스템 테마 감지 (테마가 SYSTEM일 경우)
       */
      initializeApp: async () => {
        try {
          logger.debug('🚀 [AppStore] 앱 초기화 시작...');
          
          // 1. 네트워크 상태 리스너 등록 (웹 표준 API)
          if (typeof window !== 'undefined') {
            const handleOnline = () => {
              logger.debug('✅ [AppStore] 네트워크 연결됨');
              get().setNetworkState(true);
            };
            
            const handleOffline = () => {
              logger.warn('⚠️ [AppStore] 네트워크 연결 끊김');
              get().setNetworkState(false);
            };
            
            window.addEventListener('online', handleOnline);
            window.addEventListener('offline', handleOffline);
            
            // 초기 네트워크 상태 설정
            get().setNetworkState(navigator.onLine);
          }
          
          // 2. 초기 언어 설정 동기화
          const storedLanguage = get().language;
          const language = i18nService.getCurrentLanguage?.() || storedLanguage || getTenantDefaultLanguage();
          if (storedLanguage !== language) {
            set({ language });
          }
          await i18nService.changeLanguage(language);
          logger.debug('✅ [AppStore] 초기 언어 설정 완료:', language);
          
          // 3. 시스템 테마 감지 (테마가 SYSTEM일 경우)
          const { theme } = get();
          if (theme === THEMES.SYSTEM && typeof window !== 'undefined') {
            const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
            
            // 시스템 테마 변경 감지
            const handleThemeChange = (e) => {
              logger.debug('🎨 [AppStore] 시스템 테마 변경 감지:', e.matches ? 'dark' : 'light');
              get().setTheme(THEMES.SYSTEM);
            };
            
            darkModeQuery.addEventListener('change', handleThemeChange);
            
            // 초기 시스템 테마 적용
            get().setTheme(THEMES.SYSTEM);
          }
          
          logger.debug('✅ [AppStore] 앱 초기화 완료');
          return true;
          
        } catch (error) {
          logger.error('❌ [AppStore] 앱 초기화 실패:', error);
          get().setGlobalError('앱 초기화에 실패했습니다.');
          return false;
        }
      },
      
      /**
       * 🔄 앱 상태 완전 리셋
       * - 모든 상태를 초기값으로 되돌림
       * - localStorage 완전 비움
       */
      resetApp: () => {
        logger.debug('🔄 [AppStore] 앱 상태 리셋 시작...');
        
        set({
          // 🔥 [2026-05-07] 문자열 코드로 저장한다 (예: 'en'). 과거에는 LANGUAGES.EN
      // (= {code, name, flag} 객체) 를 그대로 저장했지만 모든 consumer 가
      // language === 'en' 처럼 문자열 비교를 하기 때문에 항상 실패하면서 한국어
      // fallback 으로 떨어졌다. 모든 consumer 가 string code 를 기대하므로 여기서
      // 일관되게 string 으로 저장한다.
      language: getTenantDefaultLanguage(),
          theme: THEMES.LIGHT,
          isLoading: false,
          globalError: null,
          lastSync: null,
          dashboardStats: {
            carbonPoints: 0,
            totalMatches: 0,
            activeMeetings: 0,
            completedEvents: 0,
          },
        });
        
        // localStorage 완전 비우기
        if (typeof localStorage !== 'undefined') {
          localStorage.clear();
          logger.debug('🧹 [AppStore] localStorage 초기화 완료');
        }
        
        logger.debug('✅ [AppStore] 앱 상태 리셋 완료');
      },
      
      // ========================================
      // 🛠️ 유틸리티 메서드
      // ========================================
      
      /**
       * 현재 테마가 다크 모드인지 확인
       * @returns {boolean}
       */
      isDarkMode: () => {
        const { theme } = get();
        
        if (theme === THEMES.DARK) {
          return true;
        }
        
        if (theme === THEMES.SYSTEM && typeof window !== 'undefined') {
          return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        
        return false;
      },
      
      /**
       * 현재 언어 코드 가져오기
       * @returns {string}
       */
      getCurrentLanguage: () => {
        return get().language;
      },
      
      /**
       * 온라인 상태 확인
       * @returns {boolean}
       */
      checkOnlineStatus: () => {
        return get().isOnline;
      },
    }),
    {
      name: 'app-storage', // localStorage 키 이름
      
      // 🔥 웹용 localStorage 사용
      storage: createJSONStorage(() => localStorage),
      
      // 영속화할 상태만 선택 (성능 최적화)
      partialize: (state) => ({
        language: state.language,
        theme: state.theme,
        lastSync: state.lastSync,
      }),
      
      // 버전 관리 (스키마 변경 시 자동 마이그레이션)
      // v2: 디폴트 언어를 한국어 → 영어로 전환.
      // v3: language 필드를 LANGUAGES.* 객체 → string code 로 정규화. 과거 객체
      //     형태로 저장된 값은 모든 consumer 의 string 비교를 통과하지 못해
      //     한국어 fallback 으로 떨어지는 버그가 있었다 (예: SurveyPage.t()).
      version: 3,

      // 마이그레이션 함수 (향후 버전 업그레이드 시 사용)
      migrate: (persistedState, version) => {
        if (!persistedState) return persistedState;
        if (version < 2) {
          // v2: 영어 디폴트 전환 — 이전에 영속된 language 값을 무효화해 새
          // 기본값 ('en') 으로 떨어지게 한다.
          delete persistedState.language;
        }
        if (version < 3) {
          // v3: 객체 → string code 정규화. 잔존하는 객체형 language 가 있으면
          // .code 로 풀어쓰고, 풀 수 없으면 삭제해 기본값('en') 으로 폴백.
          const lang = persistedState.language;
          if (lang && typeof lang === 'object') {
            if (typeof lang.code === 'string') {
              persistedState.language = lang.code;
            } else {
              delete persistedState.language;
            }
          }
        }
        return persistedState;
      },
      
      // 🔥🔥🔥 [수정] 스토어가 localStorage에서 복구된 직후 실행
      // ThemeContext와 동일한 클래스명 사용
      onRehydrateStorage: () => (state) => {
        if (state) {
          // 방어적 정규화: 어떤 경로로든 객체 형태가 살아있다면 string code 로 변환
          if (state.language && typeof state.language === 'object') {
            state.language = typeof state.language.code === 'string'
              ? state.language.code
              : getTenantDefaultLanguage();
          }
          if (typeof state.language !== 'string') {
            state.language = getTenantDefaultLanguage();
          }
          const serviceLanguage = i18nService.getCurrentLanguage?.();
          const hydratedLanguage = serviceLanguage || state.language || getTenantDefaultLanguage();
          state.language = hydratedLanguage;

          logger.debug('🔄 [AppStore] 상태 복구됨. 언어:', state.language, '테마:', state.theme);

          // 1. 언어 즉시 적용 (i18nService 동기화)
          try {
            i18nService.changeLanguage(state.language);
            logger.debug('✅ [AppStore] 복구된 언어 적용 완료:', state.language);
          } catch (error) {
            logger.error('❌ [AppStore] 언어 복구 중 오류:', error);
          }
          
          // 2. 🔥 [수정] 테마 즉시 적용 - ThemeContext와 동일한 클래스명 사용
          if (state.theme && typeof document !== 'undefined') {
            try {
              let effectiveTheme = state.theme;
              
              // 시스템 테마인 경우 실제 테마 결정
              if (state.theme === THEMES.SYSTEM) {
                const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                effectiveTheme = prefersDark ? THEMES.DARK : THEMES.LIGHT;
              }
              
              // 모든 테마 클래스 제거 후 적용
              document.body.classList.remove('theme-light', 'theme-dark', 'light-mode', 'dark-mode');
              
              if (effectiveTheme === THEMES.DARK) {
                document.body.classList.add('theme-dark', 'dark-mode');
                document.documentElement.setAttribute('data-theme', 'dark');
              } else {
                document.body.classList.add('theme-light', 'light-mode');
                document.documentElement.setAttribute('data-theme', 'light');
              }
              
              logger.debug('✅ [AppStore] 복구된 테마 적용 완료:', effectiveTheme);
            } catch (error) {
              logger.error('❌ [AppStore] 테마 복구 중 오류:', error);
            }
          }
        }
      },
    }
  )
);

export default useAppStore;

// Named export도 제공 (import 방식의 유연성)
export { useAppStore };
